import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { TargetSentence } from '@shared/ui';
import { stagePosKey, type BossStats, type SessionCard } from '../model';
import { StageAction, StageChip, StageFinish, StageTop } from './stage/StageTop';

interface MatchBossProps {
  sessionCards: SessionCard[];
  onDone: (stats: BossStats) => void;
  /** Сообщаем движку матч/промах — комбо единое на всю сессию (recall + boss). */
  onMatch: () => void;
  onMiss: () => void;
  /** Выход из тренировки с сохранением — кнопка в шапке раунда. */
  onExit: () => void;
  /** Завершить сессию без финала — как «завершить» в основной части (2026-10-07). */
  onFinish?: () => void;
  finishing?: boolean;
  busy?: boolean;
  /** Сколько пар показываем на экране одновременно. */
  batch?: number;
}

interface MPair {
  id: string;
  en: string;
  ru: string;
  /** Ключ подписи части речи («сущ.»…); null — не подписываем (other, неизвестная). */
  posKey: string | null;
  /** Простое предложение смысла пары — слева вместо слова, слово выделено. */
  sentence: string | null;
  spans: [number, number][];
}
interface ActivePair extends MPair {
  /** Ключ для независимой перетасовки правой колонки (слева — порядок входа). */
  rkey: number;
}

function shuffle<T>(arr: readonly T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Финальный раунд — матч исчезающими парами: клик по слову слева, затем по
 * переводу справа. Верно — пара схлопывается и уходит с экрана, снизу
 * подтягивается следующая. Промах не штрафует очередь, но ломает чистый раунд.
 * Два клика одинаково работают мышью и пальцем, линий между колонками нет.
 */
export function MatchBoss({
  sessionCards,
  onDone,
  onMatch,
  onMiss,
  onExit,
  onFinish,
  finishing = false,
  busy = false,
  batch = 5,
}: MatchBossProps) {
  const { t } = useTranslation();

  // Уникальные (по id = stateId) пары, перетасованы один раз.
  const allPairs = useMemo<MPair[]>(() => {
    const seen = new Set<string>();
    const uniq: MPair[] = [];
    for (const c of sessionCards) {
      if (!c || seen.has(c.id)) continue;
      seen.add(c.id);
      uniq.push({
        id: c.id,
        en: c.en.toLowerCase(),
        ru: c.ru.toLowerCase(),
        posKey: stagePosKey(c.pos),
        sentence: c.simpleExample,
        spans: c.simpleExampleSpans,
      });
    }
    return shuffle(uniq);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const total = allPairs.length;

  const withRkey = (p: MPair): ActivePair => ({ ...p, rkey: Math.random() });

  const [game, setGame] = useState(() => ({
    active: allPairs.slice(0, batch).map(withRkey),
    queue: allPairs.slice(batch),
    collected: 0,
  }));
  const [sel, setSel] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [merging, setMerging] = useState<string | null>(null);
  const [missPair, setMissPair] = useState<{ left: string; right: string } | null>(null);
  const [finished, setFinished] = useState(false);

  const startRef = useRef(performance.now());
  const missesRef = useRef(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Пустой пул (все карточки без переводов) — сразу завершаем, без зависания.
  useEffect(() => {
    if (total === 0) onDone({ packsClean: 0, totalPacks: 0, durationMs: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Все пары собраны → держим экран секунду и отдаём итог.
  useEffect(() => {
    if (total > 0 && game.collected >= total && !finished) {
      setFinished(true);
      const tid = setTimeout(() => {
        onDone({
          packsClean: missesRef.current === 0 ? 1 : 0,
          totalPacks: 1,
          durationMs: performance.now() - startRef.current,
        });
      }, 900);
      timers.current.push(tid);
    }
  }, [game.collected, total, finished, onDone]);

  const rightCards = useMemo(
    () => [...game.active].sort((a, b) => a.rkey - b.rkey),
    [game.active],
  );

  const handleLeft = (id: string) => {
    if (locked || finished) return;
    setSel((prev) => (prev === id ? null : id));
  };

  const handleRight = (id: string) => {
    if (locked || finished || !sel) return;
    if (id === sel) {
      setLocked(true);
      onMatch();
      const mid = sel;
      setSel(null);
      setMerging(mid);
      const tid = setTimeout(() => {
        setGame((g) => {
          const remaining = g.active.filter((a) => a.id !== mid);
          const queue = g.queue.slice();
          while (remaining.length < batch && queue.length) {
            remaining.push(withRkey(queue.shift() as MPair));
          }
          return { active: remaining, queue, collected: g.collected + 1 };
        });
        setMerging(null);
        setLocked(false);
      }, 520);
      timers.current.push(tid);
    } else {
      setLocked(true);
      missesRef.current += 1;
      onMiss();
      setMissPair({ left: sel, right: id });
      const tid = setTimeout(() => {
        setMissPair(null);
        setSel(null);
        setLocked(false);
      }, 460);
      timers.current.push(tid);
    }
  };

  if (total === 0) return null;

  const message = merging
    ? t('session.boss.hit', 'есть пара')
    : missPair
      ? t('session.boss.missed', 'не то — попробуй ещё')
      : '';

  return (
    <>
      <StageTop
        left={<StageChip>{t('session.boss.eyebrow', 'финальный раунд')}</StageChip>}
        center={
          <div className="st-progress">
            <div className="st-ticks-wrap">
              <div className="st-ticks">
                {Array.from({ length: total }).map((_, i) => (
                  <span key={i} className={cn('st-tick', i < game.collected ? 'done' : 'pending')} />
                ))}
              </div>
            </div>
            <span className="st-count">
              {String(game.collected).padStart(2, '0')}
              <i> / {total}</i>
            </span>
          </div>
        }
        right={
          <>
            <StageAction onClick={onExit} disabled={busy}>
              {t('session.exitShort', 'выйти')}
            </StageAction>
            {onFinish && <StageFinish onFinish={onFinish} disabled={busy} finishing={finishing} />}
          </>
        }
      />
      <main className="st-body st-boss">
        <div className="st-task">
          {sel
            ? t('session.boss.taskRight', 'теперь — перевод справа')
            : t('session.boss.taskLeft', 'выбери слово слева')}
        </div>

        <div className="st-pairs">
          <div className="st-pair-col">
            {game.active.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={locked}
                onClick={() => handleLeft(p.id)}
                className={cn(
                  'st-pair',
                  p.sentence && 'is-sentence',
                  sel === p.id && 'is-sel',
                  merging === p.id && 'is-hit',
                  missPair?.left === p.id && 'is-miss',
                )}
              >
                {p.sentence ? (
                  // Предложение прямого перевода: какой это смысл, видно по нему, а
                  // выделенное слово — то, что соотносим с переводом справа.
                  <span>
                    <TargetSentence sentence={p.sentence} spans={p.spans} word={p.en} markClassName="st-pair-target" />
                  </span>
                ) : (
                  <>
                    <span>{p.en}</span>
                    {/* «сущ.», «глаг.»… как в модуле; other и неизвестную не подписываем (ТЗ демо 1.8). */}
                    {p.posKey && <span className="st-pair-pos">{t(p.posKey)}</span>}
                  </>
                )}
              </button>
            ))}
          </div>
          <div className="st-pair-col">
            {rightCards.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={locked}
                onClick={() => handleRight(p.id)}
                className={cn(
                  'st-pair',
                  merging === p.id && 'is-hit',
                  missPair?.right === p.id && 'is-miss',
                )}
              >
                <span>{p.ru}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={cn('st-boss-msg', merging && 'is-good', missPair && 'is-bad')}>
          {finished ? t('session.boss.allDone') : message}
        </div>
        <div className="st-boss-foot">{t('session.boss.foot')}</div>
      </main>
    </>
  );
}
